import { catchSentryError } from "@utils/sentry";
import {
  storageTransferProgressPercent,
  type StorageTransferProgressInput
} from "@utils/storageTransferProgress";
import { post } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { downloadData } from "aws-amplify/storage";

import {
  handleStorageError,
  isEmptyStorageKey,
  STORAGE_BUCKET,
  STORAGE_ENDPOINT
} from "./shared";

const BLOB_URL_REVOKE_DELAY_MS = 150;

const isHttpUrl = (location: string): boolean =>
  location.startsWith("http://") || location.startsWith("https://");

const downloadBlob = (blob: Blob, filename: string): HTMLAnchorElement => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download.txt";
  const clickHandler = () => {
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.removeEventListener("click", clickHandler);
    }, BLOB_URL_REVOKE_DELAY_MS);
  };
  a.addEventListener("click", clickHandler, false);
  a.click();
  return a;
};

const mapDownloadProgress = (
  onProgress: (progress: string) => void
): ((event: StorageTransferProgressInput) => void) => {
  return (event) => {
    const pct = storageTransferProgressPercent(event);
    if (pct > 0 || event.totalBytes) {
      onProgress(`${Math.round(pct)}%`);
    }
  };
};

/** An S3 object's bytes, read through Amplify rather than fetching a URL. */
export const getFileBlob = async (
  s3Key: string,
  onProgress?: (progress: string) => void
): Promise<Blob> => {
  const { body } = await downloadData({
    options: {
      bucket: STORAGE_BUCKET,
      ...STORAGE_ENDPOINT,
      onProgress: onProgress ? mapDownloadProgress(onProgress) : undefined
    },
    path: s3Key
  }).result;
  return body.blob();
};

export const downloadFile = async (
  location: string,
  downloadName: string,
  onProgress?: (progress: string) => void
): Promise<void> => {
  if (isEmptyStorageKey(location)) return;

  try {
    let blob: Blob;
    if (isHttpUrl(location)) {
      const response = await fetch(location, { cache: "no-store" });
      blob = await response.blob();
      if (onProgress) onProgress("100%");
    } else {
      blob = await getFileBlob(location, onProgress);
    }

    const fileExtension = location
      .substring(location.lastIndexOf(".") + 1)
      .toLowerCase();
    const filenameWithExtension = `${downloadName}.${fileExtension}`;

    downloadBlob(blob, filenameWithExtension);
  } catch (error) {
    handleStorageError(error);
  }
};

export const postZipMessageData = async (keys: string[]): Promise<string> => {
  try {
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString();
    const operation = post({
      apiName: "api",
      options: {
        body: { documents: keys },
        headers: accessToken ? { Authorization: accessToken } : undefined
      },
      path: "fileZipper"
    });
    const { body } = await operation.response;
    const response = (await body.json()) as {
      presignedUrl?: string;
      data?: { presignedUrl?: string };
    };
    return response.presignedUrl || response.data?.presignedUrl || "";
  } catch (error) {
    catchSentryError(error);
    throw error;
  }
};

export const bulkDownload = async (
  keys: string[],
  onProgress?: (progress: string) => void
): Promise<void> => {
  try {
    const presignedUrl = await postZipMessageData(keys);
    await downloadFile(presignedUrl, "archive.zip", onProgress);
  } catch (error) {
    handleStorageError(error);
  }
};
